import React, { useState } from 'react';
import classnames from 'classnames';
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Requirement from './Requirement';
import SupportDocs from './SupportDocs';
import ProcurementGtcTab from '../../../../shared/offer-domain/procurement/ProcurementGtcTab';
import SubmitConfirmModal from '../SubmitConfirmModal';
import useInviteDmut from '../../hooks/useInviteDmut';
import ActionButton from '../../../../Components/Common/ActionButton';
import {
  SkeletonCard,
  SkeletonTable,
} from '../../../../Components/Common/Skeleton';

const InviteDmut = ({
  procId,
  procurement,
  isLoadingProcurement,
  onRefreshProcurement,
  procurementDocuments = [],
  isLoadingDocuments,
  parcels = [],
  isLoadingParcels = false,
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const {
    documents,
    isSubmitting,
    isGeneratingDocument,
    searchText,
    setSearchText,
    sendInvitation,
    generateDocument,
  } = useInviteDmut({
    procId,
    procurement,
    documents: procurementDocuments,
    onRefreshProcurement,
  });

  const isInviteSubmitted = procurement?.isInviteDMUTCompleted === true;
  const isDownloading = isGeneratingDocument.word || isGeneratingDocument.pdf;

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleSendInvitation = async () => {
    await sendInvitation();
  };

  if (isLoadingProcurement && !procurement) {
    return (
      <>
        <SkeletonCard lines={2} className='mb-3' />
        <SkeletonTable rows={4} columns={4} />
      </>
    );
  }

  return (
    <React.Fragment>
      <div className='d-flex flex-wrap gap-2 align-items-center justify-content-between mb-4'>
        <div>
          <h5 className='mb-1 text-dark'>Invite Registered Supplier</h5>
          <p className='text-muted mb-0 fs-13'>
            Review requirements, supporting documents and GTC before sending
            invitation to Registered Supplier.
          </p>
        </div>
        <UncontrolledDropdown>
          <DropdownToggle
            color='light'
            className='border text-dark d-flex align-items-center gap-2'
            disabled={!procId || isDownloading}
          >
            {isDownloading ? (
              <>
                <Spinner size='sm' aria-hidden='true' />
                Downloading...
              </>
            ) : (
              <>
                Download
                <i className='ri-download-2-line fs-16'></i>
              </>
            )}
          </DropdownToggle>
          <DropdownMenu end>
            <DropdownItem
              onClick={() => generateDocument('word')}
              disabled={isDownloading}
              className='d-flex align-items-center gap-2'
            >
              <i className='ri-file-word-2-line fs-16'></i>
              Word
            </DropdownItem>
            <DropdownItem
              onClick={() => generateDocument('pdf')}
              disabled={isDownloading}
              className='d-flex align-items-center gap-2'
            >
              <i className='ri-file-pdf-2-line fs-16'></i>
              PDF
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>

      <div className='checkout-tab'>
        <Nav
          tabs
          className='nav-tabs-custom nav-primary mt-1 mb-3 align-items-center flex-nowrap'
        >
          <NavItem>
            <NavLink
              tag='button'
              type='button'
              className={classnames(
                'cursor-pointer d-inline-flex align-items-center flex-shrink-0',
                { active: activeTab === '1' }
              )}
              onClick={() => toggleTab('1')}
            >
              <i
                className='ri-file-list-3-line me-1 align-bottom'
                aria-hidden='true'
              ></i>
              <span className='fw-semibold'>Requirements</span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              tag='button'
              type='button'
              className={classnames(
                'cursor-pointer d-inline-flex align-items-center flex-shrink-0',
                { active: activeTab === '2' }
              )}
              onClick={() => toggleTab('2')}
            >
              <i
                className='ri-file-text-line me-1 align-bottom'
                aria-hidden='true'
              ></i>
              <span className='fw-semibold'>Supporting Documents</span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              tag='button'
              type='button'
              className={classnames(
                'cursor-pointer d-inline-flex align-items-center flex-shrink-0',
                { active: activeTab === '3' }
              )}
              onClick={() => toggleTab('3')}
            >
              <i
                className='ri-scales-3-line me-1 align-bottom'
                aria-hidden='true'
              ></i>
              <span className='fw-semibold'>GTC</span>
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {activeTab === '1' && (
            <TabPane tabId='1'>
              <Requirement
                procurement={procurement}
                parcels={parcels}
                isLoadingParcels={isLoadingParcels}
              />
            </TabPane>
          )}
          {activeTab === '2' && (
            <TabPane tabId='2'>
              <SupportDocs
                documents={documents}
                isLoading={isLoadingDocuments}
                searchText={searchText}
                onSearchChange={setSearchText}
                onClearSearch={() => setSearchText('')}
                isReadOnly={isInviteSubmitted}
                showDmutBadge
              />
            </TabPane>
          )}
          {activeTab === '3' && (
            <TabPane tabId='3'>
              <ProcurementGtcTab procurementId={procId} />
            </TabPane>
          )}
        </TabContent>
      </div>

      {!isInviteSubmitted && (
        <div className='req-actionbar flex-wrap'>
          <ActionButton
            variant='primary'
            icon='ri-send-plane-line'
            loading={isSubmitting}
            onClick={() => setShowConfirmModal(true)}
          >
            Send Invitation to Registered Supplier
          </ActionButton>
        </div>
      )}

      <SubmitConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          setShowConfirmModal(false);
          await handleSendInvitation();
        }}
        title='Send invitation to registered suppliers?'
        message='The invitation is sent to the registered suppliers right away and this step is then locked. It cannot be undone.'
        confirmLabel='Send invitation'
        isSubmitting={isSubmitting}
      />
    </React.Fragment>
  );
};

export default InviteDmut;
